import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronUp } from "lucide-react";

const UPDATES = [
  {
    version: "v2.2.1",
    date: "December 7, 2025",
    get image() {
      return `/assets/images/${this.version}.png`;
    },
    summary: "Bug fixes and improvements",
    changes: [
      "Added Spectrum Key exchange in the Spectrum Box section.",
      "The timer for the final boss in Hell Mode is now 30 seconds per stage.",
      "The game engine was updated to improve stability and performance.",
      "Fixed a bug in the Cyan world.",
      "Fixed a bug in the Red world.",
      "Fixed an audio issue in Hell mode.",
      "Fixed an audio issue in Easy mode",
      "Fixed a bug in the final boss in Hell mode.",
      "Fixed text issues in the Settings section.",
      "Minor bug fixes.",
    ],
  },
  {
    version: "v2.2.0",
    date: "October 28, 2025",
    get image() {
      return `/assets/images/${this.version}.png`;
    },
    summary: "UI overhaul, rewards upgrade and major fixes",
    changes: [
      "Redesigned the Shop section",
      "Redesigned the Settings section",
      "Redesigned Player vs Bot interfaces",
      "Added a field to change your username",
      "Increased reward for winning a player vs bot match to 500 coins",
      "Updated Android version to v15",
      "Minor bug fixes",
    ],
  },
  {
    version: "v2.1.9.2",
    date: "April 24, 2023",
    get image() {
      return `/assets/images/${this.version}.png`;
    },
    summary: "Fixes across worlds and improved luminosity",
    changes: [
      "Fixed a problem in the Red World",
      "Fixed a problem in the Blue World",
      "Fixed a problem in Dark World S2",
      "Luminosity has been reduced in the four Dark Worlds",
    ],
  },
  {
    version: "v2.1.9",
    date: "March 11, 2023",
    get image() {
      return `/assets/images/${this.version}.png`;
    },
    summary: "Crash fix, Android 13 support and minor fixes",
    changes: [
      "Fixed a bug that caused the game to crash",
      "The game has been updated to support Android 13",
      "Other minor bugs fixed",
    ],
  },
  {
    version: "v2.1.8",
    date: "December 27, 2022",
    get image() {
      return `/assets/images/${this.version}.png`;
    },
    summary: "Control tweaks, new music and Android 12L–13 support",
    changes: [
      "Added an option to choose the position of the controls in the interface",
      "The control Swipe has been modified and now works correctly",
      "The music in these sections have been changed: Menu, Purple World, Green World and Battle",
      "The game has been updated to support Android 12L and 13",
      "Other bugs have been fixed",
    ],
  },
  {
    version: "v2.1.5",
    date: "May 6, 2022",
    get image() {
      return `/assets/images/${this.version}.png`;
    },
    summary: "Minor fixes, UI tweaks and general optimization",
    changes: ["Minor bug fixes", "Minor interface changes", "Optimization"],
  },
  {
    version: "v2.1.2",
    date: "April 2, 2022",
    get image() {
      return `/assets/images/${this.version}.png`;
    },
    summary: "New missions added and several bug fixes",
    changes: ["Missions have been added", "Bug fixes"],
  },
  {
    version: "v2.1.0",
    date: "February 18, 2022",
    get image() {
      return `/assets/images/${this.version}.png`;
    },
    summary: "Major content update: new levels, modes and effects",
    changes: [
      "New interface in the levels",
      "Added 80 new levels (Dark World Series)",
      "Added 7 new languages",
      "Added an effect when the player dies",
      "Added Keyboard and Touch mode",
      "New achievements have been added",
      "The movement of the block has been modified",
      "Player vs Bot game mode has been modified",
      "The level change system has been modified",
      "All worlds have been modified and new effects have been added",
      "Optimization",
    ],
  },
  {
    version: "v2.0.0",
    date: "July 1, 2021",
    get image() {
      return `/assets/images/${this.version}.png`;
    },
    summary: "New modes, improved graphics and UI enhancements",
    changes: [
      "Added a new game mode (Player vs CPU)",
      "Added new difficulty modes (Hell & Easy)",
      "Implementation of a new menu",
      "Improvement in the graphic level",
      "Graphic corrections",
      "Improved controls",
      "UI improvements",
      "Minor bugs fixed",
    ],
  },
  {
    version: "v1.1.3",
    date: "June 9, 2021",
    get image() {
      return `/assets/images/${this.version}.png`;
    },
    summary: "Minor bugs fixes",
    changes: ["Minor bugs fixed"],
  },
  {
    version: "v1.1.2",
    date: "May 22, 2021",
    get image() {
      return `/assets/images/${this.version}.png`;
    },
    summary: "General optimization and improvements",
    changes: ["Optimization", "Fixes and improvements"],
  },
  {
    version: "v1.1.0",
    date: "April 3, 2021",
    get image() {
      return `/assets/images/${this.version}.png`;
    },
    summary: "Updated shop, new menu effects and rewards",
    changes: [
      "The shop interface has been modified",
      "A skin has been awarded as a reward to players",
      "New effects have been implemented in the main menu",
      "New menus have been added to the main menu",
      "New clues have been added about Float Block Part 2",
      "Minor bug fixes",
    ],
  },
  {
    version: "v1.0.1",
    date: "January 19, 2021",
    get image() {
      return `/assets/images/${this.version}.png`;
    },
    summary: "Visual goal update, auto-GPS, balance tweaks",
    changes: [
      "Now the goal is visible with a color and effect",
      "Now Google Play Services will start automatically",
      "Now the difficulty of various levels has been reduced",
      "Minor bug fixes",
    ],
  },
  {
    version: "v1.0.0",
    date: "January 9, 2021",
    get image() {
      return `/assets/images/${this.version}.png`;
    },
    summary: "Official launch of Float Block",
    changes: ["Launch of Float Block: Bloxorz Puzzle"],
  },
];

const ACTIVE_VERSION = "v2.2.1";
const PAGE_SIZE = 9;

const detailsVariants = {
  collapsed: { opacity: 0, height: 0, y: -4 },
  open: { opacity: 1, height: "auto", y: 0 },
};

export function ReleaseHistory() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showRest, setShowRest] = useState(false);

  const current = UPDATES[currentIndex];

  const visibleUpdates = showRest
    ? UPDATES.slice(PAGE_SIZE)
    : UPDATES.slice(0, PAGE_SIZE);

  const handleSelect = (globalIndex) => {
    setCurrentIndex(globalIndex);
  };

  const handleToggleRest = () => {
    if (!showRest) {
      const firstRestIndex = PAGE_SIZE;
      if (UPDATES[firstRestIndex]) {
        setCurrentIndex(firstRestIndex);
      }
      setShowRest(true);
    } else {
      setCurrentIndex(0);
      setShowRest(false);
    }
  };

  const hasRest = UPDATES.length > PAGE_SIZE;

  const PAGE_SIZE_MOBILE = 3;
  const [mobilePage, setMobilePage] = useState(0);

  const total = UPDATES.length;
  const totalPages = Math.ceil(total / PAGE_SIZE_MOBILE);

  const startIndex = mobilePage * PAGE_SIZE_MOBILE;
  const pageUpdates = UPDATES.slice(
    startIndex,
    startIndex + PAGE_SIZE_MOBILE
  );

  const isLastPage = mobilePage === totalPages - 1;
  const hasMorePages = totalPages > 1;

  return (
    <>
      {/* MOBILE (< md) */}
      <div className="block md:hidden">
        <section
          className="relative w-full pb-10 mb-20 overflow-hidden"
          aria-label="Release history"
        >
          <div className="pointer-events-none absolute inset-0 -z-10 opacity-70">
            <div className="absolute -top-32 left-10 w-64 h-64 rounded-full bg-[radial-gradient(circle_at_center,#a855f7_0,transparent_60%)] blur-3xl" />
            <div className="absolute top-1/3 right-[-40px] w-64 h-64 rounded-full bg-[radial-gradient(circle_at_center,#60a5fa_0,transparent_60%)] blur-3xl" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(129,140,248,0.38)_0,_transparent_55%)]" />
          </div>

          <div className="pointer-events-none absolute inset-0 -z-10 opacity-[0.18] mix-blend-soft-light bg-[linear-gradient(to_right,rgba(148,163,184,0.2)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.2)_1px,transparent_1px)] bg-[size:40px_40px]" />

          <div className="max-w-md mx-auto px-4 relative z-10">
            <div className="mb-8 text-center">
              <div className="inline-flex relative">
                <motion.div
                  className="absolute inset-0 -m-2 rounded-full bg-[#8b5cf6]/30 blur-xl pointer-events-none"
                  animate={{
                    opacity: [0.18, 0.4, 0.18],
                    scale: [0.96, 1.03, 0.96],
                  }}
                  transition={{
                    duration: 3.8,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />

                <motion.p
                  className="relative overflow-hidden inline-flex items-center gap-2 px-3 py-1 
                  rounded-full text-[10px] uppercase tracking-[0.3em] 
                  bg-white/5 text-violet-200/85 border border-violet-300/40 backdrop-blur"
                  animate={{
                    scale: [1, 1.02, 1],
                  }}
                  transition={{
                    duration: 3.8,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                >
                  <motion.span
                    className="inline-block w-1.5 h-1.5 rounded-full bg-[#c4b5fd] shadow-[0_0_8px_#c4b5fd]"
                    animate={{
                      opacity: [0.6, 1, 0.6],
                      scale: [1, 1.25, 1],
                    }}
                    transition={{
                      duration: 3.8,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                  Release history
                  <motion.span
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        "linear-gradient(120deg, transparent, rgba(139,92,246,0.12), rgba(192,132,252,0.10), rgba(168,85,247,0.12), transparent)",
                    }}
                    animate={{ x: ["-160%", "160%"] }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                </motion.p>
              </div>

              <h2
                className="mt-4 text-2xl font-extrabold text-transparent 
                bg-clip-text bg-gradient-to-r from-[#8b5cf6] via-[#c084fc] to-[#60a5fa] 
                drop-shadow-[0_0_22px_rgba(129,140,248,0.7)]"
              >
                Discover the full evolution
              </h2>

              <p className="mt-3 text-sm text-slate-300/85 max-w-sm mx-auto mb-8 leading-relaxed">
                From early builds to the latest releases, this timeline shows
                every improvement, adjustment and expansion.
              </p>
            </div>

            <div className="flex flex-col gap-8">
              <div className="relative">
                <motion.div
                  key={mobilePage}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                  className="space-y-3"
                >
                  {pageUpdates.map((u, index) => {
                    const globalIndex = startIndex + index;
                    const isSelected = globalIndex === currentIndex;
                    const isActiveVersion = u.version === ACTIVE_VERSION;

                    return (
                      <button
                        key={u.version}
                        type="button"
                        onClick={() => handleSelect(globalIndex)}
                        className={`relative w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left transition-all duration-200
                    ${
                      isSelected
                        ? "bg-[#1b102d]/90 border border-violet-400/70 shadow-[0_0_18px_rgba(139,92,246,0.9)]"
                        : "border border-transparent hover:bg-white/5 hover:border-slate-700/70"
                    }
                  `}
                      >
                        <div className="relative flex-shrink-0">
                          <div
                            className={`w-3.5 h-3.5 rounded-full border-2 transition-all duration-200
                        ${
                          isActiveVersion
                            ? "border-[#c4b5fd] bg-[#a855f7] shadow-[0_0_16px_rgba(192,132,252,1)]"
                            : "border-slate-500 bg-slate-800"
                        }
                      `}
                          />
                          {isActiveVersion && (
                            <div className="absolute inset-0 -m-2 rounded-full bg-[#a855f7]/35 blur-xl" />
                          )}
                        </div>

                        {isSelected && (
                          <span className="text-[#c4b5fd] text-base -ml-1">
                            ➤
                          </span>
                        )}

                        <div className="flex flex-col min-w-0">
                          <span
                            className={`text-[11px] uppercase tracking-[0.22em] ${
                              isSelected
                                ? "text-[#c4b5fd]"
                                : "text-slate-400"
                            }`}
                          >
                            {u.date}
                          </span>

                          <div className="flex items-center gap-2 min-w-0">
                            <span
                              className={`text-sm font-medium truncate ${
                                isSelected
                                  ? "text-slate-50"
                                  : "text-slate-200"
                              }`}
                            >
                              {u.version}
                            </span>

                            {isActiveVersion && (
                              <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.16em] bg-[radial-gradient(circle_at_top_left,#c4b5fd_0,#8b5cf6_40%,#60a5fa_100%)] text-slate-50 shadow-[0_0_12px_rgba(129,140,248,0.9)] border border-white/5">
                                <span className="whitespace-nowrap">
                                  Active
                                </span>
                              </span>
                            )}
                          </div>

                          <span className="text-[11px] text-slate-400 line-clamp-1">
                            {u.summary}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </motion.div>

                {hasMorePages && (
                  <div className="mt-4 flex items-center justify-center">
                    <motion.button
                      type="button"
                      onClick={() => {
                        if (isLastPage) {
                          setMobilePage(0);
                        } else {
                          setMobilePage((prev) => prev + 1);
                        }
                      }}
                      whileTap={{ scale: 0.92 }}
                      whileHover={{ y: -1 }}
                      className="inline-flex items-center justify-center gap-1.5 rounded-full bg-white/5 border border-slate-700/70 px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] text-slate-200 hover:border-violet-400/80 hover:text-violet-100 shadow-[0_0_18px_rgba(15,23,42,0.9)]"
                    >
                      {isLastPage ? (
                        <>
                          <ChevronUp className="w-3.5 h-3.5" />
                          <span>First updates</span>
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-3.5 h-3.5" />
                          <span>More updates</span>
                        </>
                      )}
                    </motion.button>
                  </div>
                )}
              </div>

              <div className="relative">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={current.version}
                    initial={{ opacity: 0, y: 14, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.98 }}
                    transition={{ duration: 0.35, ease: "easeOut" }}
                    className="w-full rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl overflow-hidden shadow-[0_0_30px_rgba(0,0,0,0.95)]"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <motion.img
                        key={current.image}
                        src={current.image}
                        alt={`Update ${current.version}`}
                        className="w-full h-full object-cover"
                        initial={{ scale: 1.02 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.6, ease: "easeOut" }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-transparent" />

                      <motion.div
                        className="absolute bottom-3 left-4 flex flex-wrap items-center gap-2"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 0.26,
                          ease: "easeOut",
                          delay: 0.05,
                        }}
                      >
                        <motion.span
                          whileHover={{ scale: 1.03 }}
                          className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-semibold bg-white/10 text-slate-100 border border-white/15 backdrop-blur-sm"
                        >
                          Build {current.version}
                        </motion.span>

                        <motion.span
                          whileHover={{ scale: 1.03 }}
                          className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-medium bg-white/10 text-slate-100 border border-white/15 backdrop-blur-sm"
                        >
                          {current.date}
                        </motion.span>

                        {current.version === ACTIVE_VERSION && (
                          <motion.span
                            initial={{ opacity: 0, y: 6, scale: 0.96 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            transition={{ duration: 0.22, ease: "easeOut" }}
                            whileHover={{ scale: 1.04 }}
                            className="inline-flex items-center gap-1 rounded-full px-3.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] bg-[radial-gradient(circle_at_top_left,#8b5cf6_35%,#60a5fa_100%)] text-slate-50 shadow-[0_0_18px_rgba(129,140,248,0.95)] border border-white/5 relative overflow-hidden"
                          >
                            <span className="pointer-events-none absolute inset-0 bg-[linear-gradient(120deg,rgba(255,255,255,0.32),transparent_40%,rgba(255,255,255,0.18))] opacity-40 mix-blend-screen" />
                            <span className="relative w-1.5 h-1.5 rounded-full bg-[#f9fafb] shadow-[0_0_10px_rgba(248,250,252,1)]" />
                            <span className="relative whitespace-nowrap">
                              Active version
                            </span>
                          </motion.span>
                        )}
                      </motion.div>
                    </div>

                    <div className="p-4">
                      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                        <div className="max-w-full">
                          <p className="text-[10px] uppercase tracking-[0.22em] text-slate-400 mb-1">
                            Release
                          </p>
                          <h3 className="text-base font-semibold text-slate-50">
                            {current.summary}
                          </h3>
                        </div>
                      </div>

                      <motion.div
                        key={`${current.version}-details`}
                        initial="collapsed"
                        animate="open"
                        variants={detailsVariants}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden"
                      >
                        <div className="mt-3 pt-3 border-t border-slate-800">
                          <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-2">
                            Changelog
                          </p>
                          <ul className="space-y-2 text-[13px] text-slate-200">
                            {current.changes.map((change, idx) => (
                              <li
                                key={idx}
                                className="flex items-start gap-2 group"
                              >
                                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#8b5cf6] border border-[#c084fc]/40 shadow-[0_0_6px_#8b5cf6] flex-shrink-0" />
                                <span className="leading-relaxed text-slate-300 group-hover:text-slate-50 transition-colors">
                                  {change}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </motion.div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>

          <style>{`
          .line-clamp-1 {
            display: -webkit-box;
            -webkit-line-clamp: 1;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
        `}</style>
        </section>
      </div>

      {/* TABLET (md ≤ width < lg) */}
      <div className="hidden md:block lg:hidden">
        <section
          className="relative w-full md:py-11 mb-32 overflow-hidden"
          aria-label="Release history"
        >
          <div className="pointer-events-none absolute inset-0 -z-10 opacity-70">
            <div className="absolute -top-32 left-10 w-72 h-72 rounded-full bg-[radial-gradient(circle_at_center,#a855f7_0,transparent_60%)] blur-3xl" />
            <div className="absolute top-1/3 right-0 w-80 h-80 rounded-full bg-[radial-gradient(circle_at_center,#60a5fa_0,transparent_60%)] blur-3xl" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(129,140,248,0.38)_0,_transparent_55%)]" />
          </div>

          <div className="pointer-events-none absolute inset-0 -z-10 opacity-[0.18] mix-blend-soft-light bg-[linear-gradient(to_right,rgba(148,163,184,0.2)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.2)_1px,transparent_1px)] bg-[size:48px_48px]" />

          <div className="max-w-6xl mx-auto px-4 md:px-8 relative z-10">
            <div className="mb-10 md:mb-12 text-center">
              <div className="inline-flex relative">
                <motion.div
                  className="absolute inset-0 -m-2 rounded-full bg-[#8b5cf6]/30 blur-xl pointer-events-none"
                  animate={{
                    opacity: [0.18, 0.4, 0.18],
                    scale: [0.96, 1.03, 0.96],
                  }}
                  transition={{
                    duration: 3.8,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />

                <motion.p
                  className="relative overflow-hidden inline-flex items-center gap-2 px-3 py-1 
                    rounded-full text-[11px] uppercase tracking-[0.35em] 
                    bg-white/5 text-violet-200/85 border border-violet-300/40 backdrop-blur"
                  animate={{
                    scale: [1, 1.02, 1],
                  }}
                  transition={{
                    duration: 3.8,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                >
                  <motion.span
                    className="inline-block w-1.5 h-1.5 rounded-full bg-[#c4b5fd] shadow-[0_0_8px_#c4b5fd]"
                    animate={{
                      opacity: [0.6, 1, 0.6],
                      scale: [1, 1.25, 1],
                    }}
                    transition={{
                      duration: 3.8,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                  Release history
                  <motion.span
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        "linear-gradient(120deg, transparent, rgba(139,92,246,0.12), rgba(192,132,252,0.10), rgba(168,85,247,0.12), transparent)",
                    }}
                    animate={{ x: ["-160%", "160%"] }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                </motion.p>
              </div>

              <h2
                className="mt-6 text-4xl md:text-5xl font-extrabold text-transparent 
                    bg-clip-text bg-gradient-to-r from-[#8b5cf6] via-[#c084fc] to-[#60a5fa] 
                    drop-shadow-[0_0_26px_rgba(129,140,248,0.7)]"
              >
                Discover the full evolution
              </h2>

              <p className="mt-4 text-sm md:text-base text-slate-300/85 max-w-2xl mx-auto mb-[70px]">
                From early builds to the latest releases,
                <br />
                this timeline shows every improvement, adjustment and expansion.
              </p>
            </div>
            <div className="grid grid-cols-1 ml-14 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.4fr)] gap-10 md:gap-12 items-start">
              <div className="relative">
                <div
                  className="absolute left-5 top-4 bottom-4 hidden md:block"
                  aria-hidden="true"
                >
                  <div className="w-px h-full bg-gradient-to-b from-[#8b5cf6]/80 via-slate-600/70 to-transparent" />
                </div>

                <motion.div
                  key={showRest ? "rest" : "first"}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                  className="space-y-3"
                >
                  {visibleUpdates.map((u, index) => {
                    const globalIndex = showRest ? PAGE_SIZE + index : index;
                    const isSelected = globalIndex === currentIndex;
                    const isActiveVersion = u.version === ACTIVE_VERSION;

                    return (
                      <button
                        key={u.version}
                        type="button"
                        onClick={() => handleSelect(globalIndex)}
                        className={`relative w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left transition-all duration-200
                          ${
                            isSelected
                              ? "bg-[#1b102d]/90 border border-violet-400/70 shadow-[0_0_22px_rgba(139,92,246,0.9)]"
                              : "border border-transparent hover:bg-white/5 hover:border-slate-700/70"
                          }
                        `}
                      >
                        <div className="relative flex-shrink-0">
                          <div
                            className={`w-3.5 h-3.5 rounded-full border-2 transition-all duration-200
                              ${
                                isActiveVersion
                                  ? "border-[#c4b5fd] bg-[#a855f7] shadow-[0_0_16px_rgba(192,132,252,1)]"
                                  : "border-slate-500 bg-slate-800"
                              }
                            `}
                          />
                          {isActiveVersion && (
                            <div className="absolute inset-0 -m-2 rounded-full bg-[#a855f7]/35 blur-xl" />
                          )}
                        </div>

                        {isSelected && (
                          <span className="text-[#c4b5fd] text-lg -ml-1">
                            ➤
                          </span>
                        )}

                        <div className="flex flex-col min-w-0">
                          <span
                            className={`text-xs uppercase tracking-[0.22em] ${
                              isSelected
                                ? "text-[#c4b5fd]"
                                : "text-slate-400"
                            }`}
                          >
                            {u.date}
                          </span>

                          <div className="flex items-center gap-2 min-w-0">
                            <span
                              className={`text-sm font-medium truncate ${
                                isSelected
                                  ? "text-slate-50"
                                  : "text-slate-200"
                              }`}
                            >
                              {u.version}
                            </span>

                            {isActiveVersion && (
                              <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.4 text-[9px] font-semibold uppercase tracking-[0.16em] bg-[radial-gradient(circle_at_top_left,#c4b5fd_0,#8b5cf6_40%,#60a5fa_100%)] text-slate-50 shadow-[0_0_12px_rgba(129,140,248,0.9)] border border-white/5">
                                <span className="whitespace-nowrap">
                                  Active
                                </span>
                              </span>
                            )}
                          </div>

                          <span className="text-[11px] text-slate-400 line-clamp-1">
                            {u.summary}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </motion.div>

                {hasRest && (
                  <div className="mt-4 flex items-center justify-center">
                    <motion.button
                      type="button"
                      onClick={handleToggleRest}
                      whileTap={{ scale: 0.92 }}
                      whileHover={{ y: -1 }}
                      className="inline-flex items-center justify-center gap-1.5 rounded-full bg-white/5 border border-slate-700/70 px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] text-slate-200 hover:border-violet-400/80 hover:text-violet-100 shadow-[0_0_18px_rgba(15,23,42,0.9)]"
                    >
                      {showRest ? (
                        <>
                          <ChevronUp className="w-3.5 h-3.5" />
                          <span>First updates</span>
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-3.5 h-3.5" />
                          <span>More updates</span>
                        </>
                      )}
                    </motion.button>
                  </div>
                )}
              </div>

              <div className="relative">
                <div className="h-[770px]">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={current.version}
                      initial={{ opacity: 0, y: 14, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.98 }}
                      transition={{ duration: 0.35, ease: "easeOut" }}
                      className="h-full rounded-3xl w-[550px] border-white/10 bg-white/5 backdrop-blur-xl overflow-hidden shadow-[0_0_40px_rgba(0,0,0,0.95)]"
                    >
                      <div className="relative h-52 md:h-64 overflow-hidden">
                        <motion.img
                          key={current.image}
                          src={current.image}
                          alt={`Update ${current.version}`}
                          className="w-full h-full object-cover"
                          initial={{ scale: 1.02 }}
                          animate={{ scale: 1 }}
                          transition={{ duration: 0.6, ease: "easeOut" }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-transparent" />

                        <motion.div
                          className="absolute bottom-4 left-5 flex flex-wrap items-center gap-2"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{
                            duration: 0.26,
                            ease: "easeOut",
                            delay: 0.05,
                          }}
                        >
                          <motion.span
                            whileHover={{ scale: 1.03 }}
                            className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-semibold bg-white/8 text-slate-100 border border-white/15 backdrop-blur-sm"
                          >
                            Build {current.version}
                          </motion.span>

                          <motion.span
                            whileHover={{ scale: 1.03 }}
                            className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-medium bg-white/8 text-slate-100 border border-white/15 backdrop-blur-sm"
                          >
                            {current.date}
                          </motion.span>

                          {current.version === ACTIVE_VERSION && (
                            <motion.span
                              initial={{ opacity: 0, y: 6, scale: 0.96 }}
                              animate={{ opacity: 1, y: 0, scale: 1 }}
                              transition={{ duration: 0.22, ease: "easeOut" }}
                              whileHover={{ scale: 1.04 }}
                              className="inline-flex items-center gap-1 rounded-full px-3.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] bg-[radial-gradient(circle_at_top_left,#8b5cf6_35%,#60a5fa_100%)] text-slate-50 shadow-[0_0_18px_rgba(129,140,248,0.95)] border border-white/5 relative overflow-hidden"
                            >
                              <span className="pointer-events-none absolute inset-0 bg-[linear-gradient(120deg,rgba(255,255,255,0.32),transparent_40%,rgba(255,255,255,0.18))] opacity-40 mix-blend-screen" />
                              <span className="relative w-1.5 h-1.5 rounded-full bg-[#f9fafb] shadow-[0_0_10px_rgba(248,250,252,1)]" />
                              <span className="relative whitespace-nowrap">
                                Active version
                              </span>
                            </motion.span>
                          )}
                        </motion.div>
                      </div>

                      <div className="p-5 md:p-6">
                        <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                          <div className="max-w-xl">
                            <p className="text-[11px] uppercase tracking-[0.26em] text-slate-400 mb-1">
                              Release
                            </p>
                            <h3 className="text-lg md:text-xl font-semibold text-slate-50">
                              {current.summary}
                            </h3>
                          </div>
                        </div>

                        <motion.div
                          key={`${current.version}-details`}
                          initial="collapsed"
                          animate="open"
                          variants={detailsVariants}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                          className="overflow-hidden"
                        >
                          <div className="mt-4 pt-3 border-t border-slate-800">
                            <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 mb-2">
                              Changelog
                            </p>
                            <ul className="space-y-2.5 text-[13px] md:text-sm text-slate-200">
                              {current.changes.map((change, idx) => (
                                <li
                                  key={idx}
                                  className="flex items-start gap-2 group"
                                >
                                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#8b5cf6] border border-[#c084fc]/40 shadow-[0_0_6px_#8b5cf6] flex-shrink-0" />
                                  <span className="leading-relaxed text-slate-300 group-hover:text-slate-50 transition-colors">
                                    {change}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </motion.div>
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </div>

          <style>{`
            .line-clamp-1 {
              display: -webkit-box;
              -webkit-line-clamp: 1;
              -webkit-box-orient: vertical;
              overflow: hidden;
            }
          `}</style>
        </section>
      </div>

      {/* DESKTOP (lg+) */}
      <div className="hidden lg:block">
        <section
          className="relative w-full md:py-11 mb-32 overflow-hidden"
          aria-label="Release history"
        >
          <div className="pointer-events-none absolute inset-0 -z-10 opacity-70">
            <div className="absolute -top-32 left-10 w-72 h-72 rounded-full bg-[radial-gradient(circle_at_center,#a855f7_0,transparent_60%)] blur-3xl" />
            <div className="absolute top-1/3 right-0 w-80 h-80 rounded-full bg-[radial-gradient(circle_at_center,#60a5fa_0,transparent_60%)] blur-3xl" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(129,140,248,0.38)_0,_transparent_55%)]" />
          </div>

          <div className="pointer-events-none absolute inset-0 -z-10 opacity-[0.18] mix-blend-soft-light bg-[linear-gradient(to_right,rgba(148,163,184,0.2)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.2)_1px,transparent_1px)] bg-[size:48px_48px]" />

          <div className="max-w-6xl mx-auto px-4 md:px-8 relative z-10">
            <div className="mb-10 md:mb-12 text-center">
              <div className="inline-flex relative">
                <motion.div
                  className="absolute inset-0 -m-2 rounded-full bg-[#8b5cf6]/30 blur-xl pointer-events-none"
                  animate={{
                    opacity: [0.18, 0.4, 0.18],
                    scale: [0.96, 1.03, 0.96],
                  }}
                  transition={{
                    duration: 3.8,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />

                <motion.p
                  className="relative overflow-hidden inline-flex items-center gap-2 px-3 py-1 
                    rounded-full text-[11px] uppercase tracking-[0.35em] 
                    bg-white/5 text-violet-200/85 border border-violet-300/40 backdrop-blur"
                  animate={{
                    scale: [1, 1.02, 1],
                  }}
                  transition={{
                    duration: 3.8,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                >
                  <motion.span
                    className="inline-block w-1.5 h-1.5 rounded-full bg-[#c4b5fd] shadow-[0_0_8px_#c4b5fd]"
                    animate={{
                      opacity: [0.6, 1, 0.6],
                      scale: [1, 1.25, 1],
                    }}
                    transition={{
                      duration: 3.8,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                  Release history
                  <motion.span
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        "linear-gradient(120deg, transparent, rgba(139,92,246,0.12), rgba(192,132,252,0.10), rgba(168,85,247,0.12), transparent)",
                    }}
                    animate={{ x: ["-160%", "160%"] }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                </motion.p>
              </div>

              <h2
                className="mt-6 text-4xl md:text-5xl font-extrabold text-transparent 
                    bg-clip-text bg-gradient-to-r from-[#8b5cf6] via-[#c084fc] to-[#60a5fa] 
                    drop-shadow-[0_0_26px_rgba(129,140,248,0.7)]"
              >
                Discover the full evolution
              </h2>

              <p className="mt-4 text-sm md:text-base text-slate-300/85 max-w-2xl mx-auto mb-[70px]">
                From early builds to the latest releases,
                <br />
                this timeline shows every improvement, adjustment and expansion.
              </p>
            </div>
            <div className="grid grid-cols-1 ml-14 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.4fr)] gap-10 md:gap-12 items-start">
              <div className="relative">
                <div
                  className="absolute left-5 top-4 bottom-4 hidden md:block"
                  aria-hidden="true"
                >
                  <div className="w-px h-full bg-gradient-to-b from-[#8b5cf6]/80 via-slate-600/70 to-transparent" />
                </div>

                <motion.div
                  key={showRest ? "rest" : "first"}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                  className="space-y-3"
                >
                  {visibleUpdates.map((u, index) => {
                    const globalIndex = showRest ? PAGE_SIZE + index : index;
                    const isSelected = globalIndex === currentIndex;
                    const isActiveVersion = u.version === ACTIVE_VERSION;

                    return (
                      <button
                        key={u.version}
                        type="button"
                        onClick={() => handleSelect(globalIndex)}
                        className={`relative w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left transition-all duration-200
                          ${
                            isSelected
                              ? "bg-[#1b102d]/90 border border-violet-400/70 shadow-[0_0_22px_rgba(139,92,246,0.9)]"
                              : "border border-transparent hover:bg-white/5 hover:border-slate-700/70"
                          }
                        `}
                      >
                        <div className="relative flex-shrink-0">
                          <div
                            className={`w-3.5 h-3.5 rounded-full border-2 transition-all duration-200
                              ${
                                isActiveVersion
                                  ? "border-[#c4b5fd] bg-[#a855f7] shadow-[0_0_16px_rgba(192,132,252,1)]"
                                  : "border-slate-500 bg-slate-800"
                              }
                            `}
                          />
                          {isActiveVersion && (
                            <div className="absolute inset-0 -m-2 rounded-full bg-[#a855f7]/35 blur-xl" />
                          )}
                        </div>

                        {isSelected && (
                          <span className="text-[#c4b5fd] text-lg -ml-1">
                            ➤
                          </span>
                        )}

                        <div className="flex flex-col min-w-0">
                          <span
                            className={`text-xs uppercase tracking-[0.22em] ${
                              isSelected
                                ? "text-[#c4b5fd]"
                                : "text-slate-400"
                            }`}
                          >
                            {u.date}
                          </span>

                          <div className="flex items-center gap-2 min-w-0">
                            <span
                              className={`text-sm font-medium truncate ${
                                isSelected
                                  ? "text-slate-50"
                                  : "text-slate-200"
                              }`}
                            >
                              {u.version}
                            </span>

                            {isActiveVersion && (
                              <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.4 text-[9px] font-semibold uppercase tracking-[0.16em] bg-[radial-gradient(circle_at_top_left,#c4b5fd_0,#8b5cf6_40%,#60a5fa_100%)] text-slate-50 shadow-[0_0_12px_rgba(129,140,248,0.9)] border border-white/5">
                                <span className="whitespace-nowrap">
                                  Active
                                </span>
                              </span>
                            )}
                          </div>

                          <span className="text-[11px] text-slate-400 line-clamp-1">
                            {u.summary}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </motion.div>

                {hasRest && (
                  <div className="mt-4 flex items-center justify-center">
                    <motion.button
                      type="button"
                      onClick={handleToggleRest}
                      whileTap={{ scale: 0.92 }}
                      whileHover={{ y: -1 }}
                      className="inline-flex items-center justify-center gap-1.5 rounded-full bg-white/5 border border-slate-700/70 px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] text-slate-200 hover:border-violet-400/80 hover:text-violet-100 shadow-[0_0_18px_rgba(15,23,42,0.9)]"
                    >
                      {showRest ? (
                        <>
                          <ChevronUp className="w-3.5 h-3.5" />
                          <span>First updates</span>
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-3.5 h-3.5" />
                          <span>More updates</span>
                        </>
                      )}
                    </motion.button>
                  </div>
                )}
              </div>

              <div className="relative">
                <div className="h-[770px]">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={current.version}
                      initial={{ opacity: 0, y: 14, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.98 }}
                      transition={{ duration: 0.35, ease: "easeOut" }}
                      className="h-full rounded-3xl w-[550px] border-white/10 bg-white/5 backdrop-blur-xl overflow-hidden shadow-[0_0_40px_rgba(0,0,0,0.95)]"
                    >
                      <div className="relative h-52 md:h-64 overflow-hidden">
                        <motion.img
                          key={current.image}
                          src={current.image}
                          alt={`Update ${current.version}`}
                          className="w-full h-full object-cover"
                          initial={{ scale: 1.02 }}
                          animate={{ scale: 1 }}
                          transition={{ duration: 0.6, ease: "easeOut" }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-transparent" />

                        <motion.div
                          className="absolute bottom-4 left-5 flex flex-wrap items-center gap-2"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{
                            duration: 0.26,
                            ease: "easeOut",
                            delay: 0.05,
                          }}
                        >
                          <motion.span
                            whileHover={{ scale: 1.03 }}
                            className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-semibold bg-white/8 text-slate-100 border border-white/15 backdrop-blur-sm"
                          >
                            Build {current.version}
                          </motion.span>

                          <motion.span
                            whileHover={{ scale: 1.03 }}
                            className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-medium bg-white/8 text-slate-100 border border-white/15 backdrop-blur-sm"
                          >
                            {current.date}
                          </motion.span>

                          {current.version === ACTIVE_VERSION && (
                            <motion.span
                              initial={{ opacity: 0, y: 6, scale: 0.96 }}
                              animate={{ opacity: 1, y: 0, scale: 1 }}
                              transition={{ duration: 0.22, ease: "easeOut" }}
                              whileHover={{ scale: 1.04 }}
                              className="inline-flex items-center gap-1 rounded-full px-3.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] bg-[radial-gradient(circle_at_top_left,#8b5cf6_35%,#60a5fa_100%)] text-slate-50 shadow-[0_0_18px_rgba(129,140,248,0.95)] border border-white/5 relative overflow-hidden"
                            >
                              <span className="pointer-events-none absolute inset-0 bg-[linear-gradient(120deg,rgba(255,255,255,0.32),transparent_40%,rgba(255,255,255,0.18))] opacity-40 mix-blend-screen" />
                              <span className="relative w-1.5 h-1.5 rounded-full bg-[#f9fafb] shadow-[0_0_10px_rgba(248,250,252,1)]" />
                              <span className="relative whitespace-nowrap">
                                Active version
                              </span>
                            </motion.span>
                          )}
                        </motion.div>
                      </div>

                      <div className="p-5 md:p-6">
                        <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                          <div className="max-w-xl">
                            <p className="text-[11px] uppercase tracking-[0.26em] text-slate-400 mb-1">
                              Release
                            </p>
                            <h3 className="text-lg md:text-xl font-semibold text-slate-50">
                              {current.summary}
                            </h3>
                          </div>
                        </div>

                        <motion.div
                          key={`${current.version}-details`}
                          initial="collapsed"
                          animate="open"
                          variants={detailsVariants}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                          className="overflow-hidden"
                        >
                          <div className="mt-4 pt-3 border-t border-slate-800">
                            <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 mb-2">
                              Changelog
                            </p>
                            <ul className="space-y-2.5 text-[13px] md:text-sm text-slate-200">
                              {current.changes.map((change, idx) => (
                                <li
                                  key={idx}
                                  className="flex items-start gap-2 group"
                                >
                                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#8b5cf6] border border-[#c084fc]/40 shadow-[0_0_6px_#8b5cf6] flex-shrink-0" />
                                  <span className="leading-relaxed text-slate-300 group-hover:text-slate-50 transition-colors">
                                    {change}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </motion.div>
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </div>

          <style>{`
            .line-clamp-1 {
              display: -webkit-box;
              -webkit-line-clamp: 1;
              -webkit-box-orient: vertical;
              overflow: hidden;
            }
          `}</style>
        </section>
      </div>
    </>
  );
}

export default ReleaseHistory;
